import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PricesPage } from './pages/PricesPage';
// Detect user's preferred language from browser
function detectUserLanguage(): 'en' | 'ua' | 'ru' {
  const browserLangs = navigator.languages || [navigator.language];
  for (const lang of browserLangs) {
    const langCode = lang.toLowerCase().split('-')[0];
    // Ukrainian
    if (langCode === 'uk' || langCode === 'ua') {
      return 'ua';
    }
    // Russian
    if (langCode === 'ru') {
      return 'ru';
    }
    // English
    if (langCode === 'en') {
      return 'en';
    }
  }
  // Default to English
  return 'en';
}
// Check if user has already visited (to not override manual language selection)
function getStoredLanguage(): 'en' | 'ua' | 'ru' | null {
  try {
    const stored = localStorage.getItem('dentico-preferred-lang');
    if (stored === 'en' || stored === 'ua' || stored === 'ru') {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return null;
}
function setStoredLanguage(lang: 'en' | 'ua' | 'ru') {
  try {
    localStorage.setItem('dentico-preferred-lang', lang);
  } catch {
    // localStorage not available
  }
}
function LanguageRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    // Check if user has a stored preference first
    const storedLang = getStoredLanguage();
    if (storedLang) {
      navigate(`/${storedLang}`, {
        replace: true
      });
      return;
    }
    // Otherwise detect from browser
    const detectedLang = detectUserLanguage();
    setStoredLanguage(detectedLang);
    navigate(`/${detectedLang}`, {
      replace: true
    });
  }, [navigate]);
  return null;
}
function LanguageRoutes() {
  const {
    lang
  } = useParams();
  const validLangs = ['en', 'ua', 'ru'];
  if (!validLangs.includes(lang || '')) {
    return <Navigate to="/en" replace data-id="element-0" />;
  }
  // Store the language when user navigates to it (manual selection)
  useEffect(() => {
    if (lang && validLangs.includes(lang)) {
      setStoredLanguage(lang as 'en' | 'ua' | 'ru');
    }
  }, [lang]);
  return <LanguageProvider data-id="element-1">
      <Routes data-id="element-2">
        <Route index element={<HomePage data-id="element-4" />} data-id="element-3" />
        <Route path="services" element={<ServicesPage data-id="element-6" />} data-id="element-5" />
        <Route path="services/:serviceId" element={<ServiceDetailPage data-id="element-8" />} data-id="element-7" />
        <Route path="prices" element={<PricesPage data-id="element-10" />} data-id="element-9" />
        <Route path="about" element={<AboutPage data-id="element-12" />} data-id="element-11" />
        <Route path="contact" element={<ContactPage data-id="element-14" />} data-id="element-13" />
        <Route path="*" element={<Navigate to={`/${lang}`} replace data-id="element-16" />} data-id="element-15" />
      </Routes>
    </LanguageProvider>;
}
export function App() {
  return <BrowserRouter data-id="element-17">
      <Routes data-id="element-18">
        <Route path="/" element={<LanguageRedirect data-id="element-20" />} data-id="element-19" />
        <Route path="/:lang/*" element={<LanguageRoutes data-id="element-22" />} data-id="element-21" />
        <Route path="*" element={<LanguageRedirect data-id="element-24" />} data-id="element-23" />
      </Routes>
    </BrowserRouter>;
}